import { useMemo, useState } from 'react';
import type { Category } from '../lib/api';
import { Sheet } from './ui';

/**
 * A full-screen searchable list rather than a native <select>.
 *
 * A native select on iOS renders as a spinning wheel, which is miserable for
 * fifteen entries that each carry a GL code the person may need to read.
 */
export function CategorySheet({
  open, onClose, categories, onPick, currentId,
}: {
  open: boolean;
  onClose: () => void;
  categories: Category[];
  onPick: (category: Category) => void;
  currentId?: string | undefined;
}) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter(
      (c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q) || c.glCode.includes(q),
    );
  }, [categories, query]);

  return (
    <Sheet open={open} onClose={onClose} title="Choose a category">
      <input
        type="search"
        className="field mb-3"
        placeholder="Search categories"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <ul className="divide-y divide-slate-100">
        {filtered.map((c) => (
          <li key={c.id}>
            <button
              type="button"
              onClick={() => {
                onPick(c);
                onClose();
              }}
              className={`flex min-h-12 w-full items-center justify-between py-3 text-left ${
                c.id === currentId ? 'font-semibold text-brand' : ''
              }`}
            >
              <span>
                {c.name}
                {c.requiresReceipt ? (
                  <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                    receipt required
                  </span>
                ) : null}
                {c.requiresReceiptOver ? (
                  <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
                    receipt over ${c.requiresReceiptOver}
                  </span>
                ) : null}
              </span>
              <span className="text-sm text-slate-400">{c.glCode}</span>
            </button>
          </li>
        ))}
        {filtered.length === 0 ? <li className="py-6 text-center text-slate-500">No matches</li> : null}
      </ul>
    </Sheet>
  );
}
